import { Box, Stack, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { useBoolean } from 'usehooks-ts';

import { UserRefresher } from '@/components/AppRouter/UserRefresher';
import { Icon } from '@/components/Icon/Icon';
import { Page } from '@/components/Layout/Page/Page';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs/selectors';

import { Tenant } from '@/types/common';

import { getDataTestId } from '@/utils/browser';
import { PROGRESS_STEPS } from '@/utils/progressSteps';
import { isCaymanTenant } from '@/utils/tenant';
import { skipICBCheckingPage } from '@/utils/user';

import {
  AddressBody,
  CaymanCountryICBCheckingPage,
  ICBCheckingPage,
} from './components';

import { Clock } from './AddressPage.styled';

function renderICBCheckingPage(tenant?: Tenant) {
  if (isCaymanTenant(tenant)) {
    return <CaymanCountryICBCheckingPage />;
  }
  return <ICBCheckingPage />;
}

export function AddressPage() {
  const { t } = useTranslate();
  const theme = useTheme();
  const user = useAppSelector(selectLoggedInUser);
  const {
    brandingImageAddressPage,
    shouldShowLeftBrandingLogo,
    shouldShowRightBrandingLogo,
  } = useAppSelector(selectConfigsState);
  const { value: isFinished, setTrue: setFinished } = useBoolean(false);

  if (isFinished) {
    return skipICBCheckingPage(user) ? (
      <UserRefresher noLoader />
    ) : (
      renderICBCheckingPage(user?.tenant)
    );
  }

  return (
    <Page
      paperProps={{
        position: 'right',
        size: 'big',
        bgImg: brandingImageAddressPage,
        color: (theme: Theme) => theme.palette.overlay.tiber.start,
        secondColor: (theme: Theme) => theme.palette.overlay.tiber.end,
        hasLogo: shouldShowRightBrandingLogo,
        children: (
          <>
            {shouldShowRightBrandingLogo ? <Clock /> : <Box height={'100%'} />}
            <Stack direction="row" justifyContent="center" gap={1.5}>
              <Icon
                name="lock"
                color={(theme: Theme) => theme.palette.common.white}
              />
              <Typography
                variant="caption"
                color={theme.palette.common.white}
                {...getDataTestId('address-page-title')}
              >
                {t('createNewAccount.addressPage.title')}
              </Typography>
            </Stack>
          </>
        ),
      }}
      formContainerProps={{
        sx: {
          [theme.breakpoints.up('lg')]: {
            maxHeight: `100vh`,
            marginTop: theme.spacing(4),
          },
        },
      }}
      progressProps={{
        ...PROGRESS_STEPS.addressStep,
        text: `${PROGRESS_STEPS.addressStep.value}/${PROGRESS_STEPS.addressStep.total}`,
      }}
      hasLogo={shouldShowLeftBrandingLogo}
    >
      <Box pb={12}>
        <AddressBody tenant={user?.tenant} onFinished={setFinished} />
      </Box>
    </Page>
  );
}

export default AddressPage;
